import React, { useState, useEffect } from "react";
import OtpInput from "react-otp-input";
import "./OtpVerification.scss";
import logo from "../../../assets/images/MainLogo.png";
import Buttons from "../../../components/Buttons/Buttons";
import {useNavigate} from "react-router-dom";
import { UserMfaOtp } from "../../../utils/apiCalls/auth";
import Spinner from "../../../components/Spinner/Spinner";
import { getPendingUserId, setSession } from "../../../services/auth";
import { useToast } from "../../../utils/ToastContext";

const OtpVerification = () => {
  const navigate = useNavigate();
  const [otp, setOtp] = useState("");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToast();

  // Opened directly or after a refresh with no pending login (userId only
  // exists in localStorage between the sign-in step and OTP success):
  // nothing to verify, so send the user back to sign in.
  useEffect(() => {
    if (!getPendingUserId()) {
      navigate("/", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOtpChange = (value) => {
    setOtp(value);
    if (errors.otp) {
      setErrors({});
    }
  };

  const validateOtp = () => {
    if (otp.length !== 6) {
      setErrors({ otp: "Please enter complete 6-digit OTP" });
      return false;
    }
    setErrors({});
    return true;
  };

  const handleVerifyOtp = async (isStale = () => false) => {
    if (isLoading) return;
    setIsLoading(true);
    if (validateOtp()) {
      const payload = {
        otp,
        userId: getPendingUserId(),
      };
      try {
        const res = await UserMfaOtp(payload);
        if (isStale()) return;
        const { statusCode, data, message } = res;
        if (statusCode === 200) {
          const loginType = data.userId === 1 ? 1 : 2;
          setSession({
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
            userData: data,
            loginType,
          });
          navigate(loginType === 1 ? "/admin-dashboard" : "/hp-dashboard");
        } else {
          showToast(message, "error");
        }
      } catch (error) {
        if (!isStale()) showToast(error?.message, "error");
      } finally {
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  };

  // Auto-call API when all 6 OTP digits are filled
  useEffect(() => {
    let ignore = false;
    if (otp.length === 6 && !isLoading) {
      handleVerifyOtp(() => ignore);
    }
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  const handleBackToSignIn = () => {
    // localStorage returns a string, so compare as a number.
    navigate(Number(getPendingUserId()) === 1 ? "/super-admin" : "/");
  };

  return (
    <div className="hcsignin">
        {isLoading && (
        <div className="loader-container">
          <div className="loader">
            <Spinner />
          </div>
        </div>
      )}
      <div className="hcsignin_bcg">
        <div className="hcsignin_container">
          <div className="hcsignin_section">
            <div className="hcsignin_section_logo">
              <img src={logo} alt="logo" />
            </div>
            <div className="hcsignin_section_header">
              <h3>OTP Verification</h3>
              <p>Enter the 6-digit code sent to your registered email</p>
            </div>
            <div className="hcsignin_section_inputs">
              <div className="hcsignin_section_inputs_otp">
                <div className="otp-inputs-container">
                  <OtpInput
                    value={otp}
                    onChange={handleOtpChange}
                    numInputs={6}
                    inputType="tel"
                    shouldAutoFocus
                    skipDefaultStyles
                    containerStyle={{ gap: "8px" }}
                    renderInput={(props) => (
                      <input
                        {...props}
                        className="otp-input"
                        onKeyDown={(e) => {
                          props.onKeyDown(e);
                          if (e.key === "Enter") handleVerifyOtp();
                        }}
                      />
                    )}
                  />
                </div>
                {errors.otp && (
                  <span className="error">{errors.otp}</span>
                )}
              </div>
            </div>
            <div className="hcsignin_section_buttons">
              <Buttons
                text={"Verify OTP"}
                variant="primary"
                onClick={handleVerifyOtp}
                disabled={isLoading}
              />
            </div>
            <div className="hcsignin_section_back">
              <button type="button" className="back-link" onClick={handleBackToSignIn}>
                Back to Sign In
              </button>
            </div>
          </div>
        </div>
        <div className="hcsignin_bcglayout"></div>
      </div>
    </div>
  );
};

export default OtpVerification;
